import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { ProductFormState } from './types'

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useSaveProduct } from '@@partner:graphql/mutation/catalog/saveProduct.gql'
import { useFetchProductDetails } from '@@partner:graphql/query/catalog/productDetails.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  notifyOk,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'
import {
  makeProductFormState,
  mapStateToApiVariable,
  mapApiDataToState,
} from './ProductFormState'
import { ProductFormValidationModel } from './validation/ProductFormValidationModel'

import { Form, Tabs, Tag, Popover, Whisper, Button } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { formatProductStatusLabel } from '@@partner:helpers/formatters/product'
import { PRODUCT_STATUS_LABELS } from '@@partner:config/product'

import ProductInfoTab from './tabs/ProductInfoTab'
import ProductReviewsTab from './tabs/ProductReviewsTab'
import ProductOrdersTab from './tabs/ProductOrdersTab'
import ProductPricesTab from './tabs/ProductPricesTab'
import ProductStockTab from './tabs/ProductStockTab'

export interface ProductFormProps extends CrudFormProps {
  productId?: GQL_ID
  brandSlug: string
}

export default function ProductForm ({
  brandSlug,
  productId,
  onSaved
}: ProductFormProps) {
  const { formState, setFormState, setFormRef } =
    useValidatableForm<ProductFormState>(makeProductFormState)
  const [ formError, setFormError ] = useState<Record<string, any>>({})
  const [ activeTab, setActiveTab ] = useState<string>('info')
  const [ pendingStatus, setPendingStatus ] = useState<number | null>(null)
  const [ pendingIndexable, setPendingIndexable ] = useState<boolean | null>(null)
  const statusWhisperRef = useRef<any>(null)
  const indexableWhisperRef = useRef<any>(null)

  const {
    data,
    loading: loadingProduct,
    error: errorLoadingProduct
  } = useFetchProductDetails(productId)

  const [ saveProduct, { loading: loadingSaveProduct } ] = useSaveProduct()

  useEffect(() => {
    if (!loadingProduct && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.productDetails))
      )
    }
  }, [ data, loadingProduct, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleSave = useCallback(() => {
    saveProduct({
      variables: {
        productInfo: mapStateToApiVariable(formState)
      }
    })
      .then(({ data }) => {
        notifyOk('Товар сохранён')
        setFormState((state) => mergeLeftOnly([ 'id' ], state, data?.product))
        onSaved?.()
      })
      .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
  }, [ saveProduct, formState, setFormState, onSaved ])

  if (loadingProduct && !data) {
    return <AppLoader content="Загрузка товара" />
  }

  if (errorLoadingProduct) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить продукт #${productId} для редактирования`}
        error={errorLoadingProduct}
      />
    )
  }

  const statusInfo = productId ? formatProductStatusLabel(formState.status) : null

  const statusPopover = (
    <Popover style={{ width: 220, padding: 0 }}>
      <div className="p-3">
        <div className="text-sm font-semibold mb-2">Изменить статус</div>
        {PRODUCT_STATUS_LABELS.map((item) => {
          const info = formatProductStatusLabel(item.value)
          return (
            <div
              key={item.value}
              className="py-1 px-2 rounded cursor-pointer hover:bg-gray-100 flex items-center gap-2"
              onClick={() => setPendingStatus(item.value)}
            >
              <Tag
                color={info.color as any}
                size="sm"
                style={{
                  opacity: pendingStatus === item.value ? 1 : 0.6,
                  fontWeight: pendingStatus === item.value ? 'bold' : 'normal'
                }}
              >
                {info.label}
              </Tag>
            </div>
          )
        })}
        <Button
          appearance="primary"
          size="xs"
          className="mt-2 w-full"
          disabled={pendingStatus === null || pendingStatus === formState.status}
          onClick={() => {
            if (pendingStatus !== null) {
              setFormState((state) => ({ ...state, status: pendingStatus }))
              setPendingStatus(null)
              statusWhisperRef.current?.close()
            }
          }}
        >
          Сохранить
        </Button>
      </div>
    </Popover>
  )

  const indexablePopover = (
    <Popover style={{ width: 220, padding: 0 }}>
      <div className="p-3">
        <div className="text-sm font-semibold mb-2">Видимость</div>
        {[
          { value: true, label: 'Виден покупателям', color: 'green' as const },
          { value: false, label: 'Скрыт от покупателей', color: 'red' as const }
        ].map((item) => (
          <div
            key={String(item.value)}
            className="py-1 px-2 rounded cursor-pointer hover:bg-gray-100 flex items-center gap-2"
            onClick={() => setPendingIndexable(item.value)}
          >
            <Tag
              color={item.color}
              size="sm"
              style={{
                opacity: pendingIndexable === item.value ? 1 : 0.6,
                fontWeight: pendingIndexable === item.value ? 'bold' : 'normal'
              }}
            >
              {item.label}
            </Tag>
          </div>
        ))}
        <Button
          appearance="primary"
          size="xs"
          className="mt-2 w-full"
          disabled={pendingIndexable === null || pendingIndexable === formState.indexable}
          onClick={() => {
            if (pendingIndexable !== null) {
              setFormState((state) => ({ ...state, indexable: pendingIndexable }))
              setPendingIndexable(null)
              indexableWhisperRef.current?.close()
            }
          }}
        >
          Сохранить
        </Button>
      </div>
    </Popover>
  )

  return (
    <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="u-heading" style={{ marginBottom: 0, lineHeight: '1' }}>
          {productId ? (formState.name || 'Загрузка...') : 'Добавление товара'}
        </h1>
        {productId && statusInfo && (
          <div className="flex items-center gap-2" style={{ alignSelf: 'center' }}>
            <Whisper
              ref={statusWhisperRef}
              trigger="click"
              placement="bottomStart"
              speaker={statusPopover}
              onOpen={() => setPendingStatus(formState.status)}
            >
              <Tag
                color={statusInfo.color as any}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {statusInfo.label}
              </Tag>
            </Whisper>
            <Whisper
              ref={indexableWhisperRef}
              trigger="click"
              placement="bottomStart"
              speaker={indexablePopover}
              onOpen={() => setPendingIndexable(formState.indexable)}
            >
              <Tag
                color={formState.indexable ? 'green' : 'red'}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {formState.indexable ? 'Виден покупателям' : 'Скрыт от покупателей'}
              </Tag>
            </Whisper>
          </div>
        )}
      </div>
      <Form
        model={ProductFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onCheck={handleFormError}
        ref={setFormRef}
        fluid
      >
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key as string)}
          appearance="subtle"
        >
          <Tabs.Tab eventKey="info" title="Информация">
            <ProductInfoTab
              formState={formState}
              setFormState={setFormState}
              formError={formError}
              loadingProduct={loadingProduct}
              loadingSave={loadingSaveProduct}
              attributes={data?.productDetails.attributes || []}
              brandSlug={brandSlug}
              onSave={handleSave}
            />
          </Tabs.Tab>

          <Tabs.Tab eventKey="reviews" title="Отзывы">
            {data && productId && (
              <ProductReviewsTab
                brandSlug={brandSlug}
                productId={productId}
                averageRating={data.productDetails.average_rating}
                reviewCount={data.productDetails.review_count}
              />
            )}
          </Tabs.Tab>

          <Tabs.Tab eventKey="orders" title="Заказы">
            {activeTab === 'orders' && productId && <ProductOrdersTab productId={productId} />}
          </Tabs.Tab>

          <Tabs.Tab eventKey="prices" title="Цены">
            {data && (
              <ProductPricesTab
                shopIds={formState.shop_ids}
                prices={formState.prices}
                loadingProduct={loadingProduct}
              />
            )}
          </Tabs.Tab>

          <Tabs.Tab eventKey="stock" title="Остатки">
            <ProductStockTab
              formState={formState}
              setFormState={setFormState}
              loadingProduct={loadingProduct}
            />
          </Tabs.Tab>
        </Tabs>
      </Form>
    </>
  )
}
